import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDebounce } from '../../Hooks/useDebounce';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';

const FetchAutoComplete = function FetchAutoComplete({
  id = 'id-autocomplete',
  label = '',
  placeholder = '',
  width = '100%',
  selectedValue = null,
  multiple = false,
  allUpperCase = false,
  getOptionLabel = (option) => option,
  onChangeSelectedValue,
  fetchCallback,
  staticOptions = null,
  noOptionsText,
  noCustomValue = false,
  ...fieldProps //all others autocomplete props
}) {
  const intl = useIntl();
  const [inputValue, setInputValue] = useState(''); //typing value
  const [searchText, setSearchText] = useState(''); //what the user typed, not a picked option's label
  const [options, setOptions] = useState([]);
  const [inPending, setInPending] = useState(false);
  const [debouncedValue] = useDebounce(searchText, 600); //wait after finish typing
  // Options come only after typing: say so, instead of an empty list that reads as "nothing here".
  const searchesOnType = !Array.isArray(staticOptions) && Boolean(fetchCallback);
  const fieldPlaceholder =
    placeholder || (searchesOnType ? intl.messages.TYPE_TO_SEARCH : '');
  const autocompleteValue = multiple
    ? Array.isArray(selectedValue)
      ? selectedValue
      : selectedValue
        ? [selectedValue]
        : []
    : selectedValue;

  const handleChangeInputValue = (event, newValue, reason) => {
    const value = allUpperCase ? newValue.toUpperCase() : newValue;
    setInputValue(value);
    if (reason === 'input') setSearchText(value);
  };

  useEffect(() => {
    if (Array.isArray(staticOptions)) {
      setOptions(staticOptions);
    }
  }, [staticOptions]);

  useEffect(() => {
    if (Array.isArray(staticOptions)) {
      return;
    }

    if (!fetchCallback) {
      return;
    }

    if (debouncedValue) {
      setInPending(true);
      fetchCallback(
        debouncedValue,
        (res) => {
          setOptions(
            res.length > 0 ? res : noCustomValue ? [] : [debouncedValue]
          );
          return Promise.resolve('loaded');
        },
        () => {
          setOptions([]);
          return Promise.resolve('loaded');
        }
      ).finally(() => {
        setInPending(false);
      });
    }
  }, [debouncedValue]);

  return (
    <Autocomplete
      id={id}
      multiple={multiple}
      loading={inPending}
      value={autocompleteValue}
      onChange={onChangeSelectedValue}
      inputValue={inputValue}
      onInputChange={handleChangeInputValue}
      disablePortal
      disableCloseOnSelect={multiple}
      filterSelectedOptions={multiple}
      options={options}
      fullWidth={width === '100%'}
      sx={{ width: width !== '100%' ? width : null }}
      getOptionLabel={getOptionLabel}
      noOptionsText={
        searchesOnType && !searchText
          ? intl.messages.TYPE_TO_SEARCH
          : (noOptionsText ?? intl.messages.NO_OPTIONS)
      }
      renderInput={(params) => (
        <TextField
          {...params}
          {...fieldProps}
          label={label}
          variant="outlined"
          placeholder={fieldPlaceholder}
          slotProps={{
            ...params.slotProps,
            // An outlined field hides its placeholder until the label floats; keep it floated so
            // the hint is read before the click, not after.
            inputLabel: {
              ...params.slotProps.inputLabel,
              ...(fieldPlaceholder ? { shrink: true } : {}),
            },
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {inPending ? <CircularProgress size={20} /> : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
            formHelperText: {
              sx: {
                whiteSpace: 'noWrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            },
          }}
        />
      )}
    />
  );
};

export default FetchAutoComplete;
